import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { useAuth } from "@/context/AuthContext";
import { CalendarClock, Plus, CheckCircle2, XCircle, MessageSquarePlus } from "lucide-react";

const LIGHT_STYLES = {
  GREEN: "bg-emerald-100 text-emerald-700 border-emerald-200",
  AMBER: "bg-amber-100 text-amber-700 border-amber-200",
  RED: "bg-red-100 text-red-700 border-red-200",
  DONE: "bg-gray-100 text-gray-600 border-gray-200",
};

const STATUS_LABEL = {
  ACTIVE: "Active",
  AUTO_FLAGGED: "Action needed",
  PASSED: "Passed",
  NOT_PASSED: "Not passed",
};

function InitiateDialog({ employees, onSaved }) {
  const [open, setOpen] = useState(false);
  const [user_id, setUserId] = useState("");
  const [start_date, setStart] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!user_id) { toast.error("Select an employee"); return; }
    setBusy(true);
    try {
      const body = { user_id };
      if (start_date) body.start_date = start_date;
      await api.post("/trials/initiate", body);
      toast.success("90-day trial opened");
      onSaved();
      setOpen(false);
      setUserId(""); setStart("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button data-testid="trial-initiate-btn" onClick={() => setOpen(true)} className="bg-[#1A2744] hover:bg-[#0F1A30]">
        <Plus className="h-4 w-4 mr-2" /> Open trial
      </Button>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Open 90-day trial</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label>Employee</Label>
            <Select value={user_id} onValueChange={setUserId}>
              <SelectTrigger data-testid="trial-init-user"><SelectValue placeholder="Choose employee" /></SelectTrigger>
              <SelectContent>
                {employees.map((e) => (
                  <SelectItem key={e.id} value={e.id}>{e.employee_code} · {e.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Start date</Label>
            <Input data-testid="trial-init-start" type="date" value={start_date} onChange={(e) => setStart(e.target.value)} />
            <p className="text-xs text-gray-500 mt-1">Defaults to the employee&rsquo;s record start date.</p>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="submit" data-testid="trial-init-submit" disabled={busy} className="bg-[#1A2744] hover:bg-[#0F1A30]">
              {busy ? "Opening…" : "Open trial"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function CheckinDialog({ trial, onSaved }) {
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!notes.trim()) { toast.error("Notes required"); return; }
    setBusy(true);
    try {
      await api.post(`/trials/${trial.id}/checkin`, { notes });
      toast.success("Check-in recorded");
      setOpen(false);
      setNotes("");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button data-testid={`trial-checkin-${trial.id}`} size="sm" variant="outline" onClick={() => setOpen(true)}>
        <MessageSquarePlus className="h-3.5 w-3.5 mr-1" /> Check-in
      </Button>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Mid-trial check-in · {trial.user_name}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label>Notes</Label>
            <Textarea data-testid="trial-checkin-notes" rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="How is the employee tracking against the role expectations?" />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="submit" data-testid="trial-checkin-submit" disabled={busy} className="bg-[#1A2744] hover:bg-[#0F1A30]">
              {busy ? "Saving…" : "Save check-in"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function FinalReviewDialog({ trial, onSaved }) {
  const [open, setOpen] = useState(false);
  const [decision, setDecision] = useState("PASSED");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!notes.trim()) { toast.error("Notes required"); return; }
    setBusy(true);
    try {
      await api.post(`/trials/${trial.id}/final-review`, { decision, notes });
      toast.success(decision === "PASSED" ? "Confirmed — trial passed" : "Recorded — trial not passed");
      setOpen(false);
      setNotes("");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button data-testid={`trial-final-${trial.id}`} size="sm" className="bg-[#1A2744] hover:bg-[#0F1A30]" onClick={() => setOpen(true)}>
        Final review
      </Button>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Final review · {trial.user_name}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label>Decision</Label>
            <div className="flex gap-2 mt-2">
              <Button type="button" variant={decision === "PASSED" ? "default" : "outline"} className={decision === "PASSED" ? "bg-emerald-600 hover:bg-emerald-700" : ""} onClick={() => setDecision("PASSED")}>
                <CheckCircle2 className="h-4 w-4 mr-2" /> Passed
              </Button>
              <Button type="button" variant={decision === "NOT_PASSED" ? "default" : "outline"} className={decision === "NOT_PASSED" ? "bg-red-600 hover:bg-red-700" : ""} onClick={() => setDecision("NOT_PASSED")}>
                <XCircle className="h-4 w-4 mr-2" /> Not passed
              </Button>
            </div>
          </div>
          <div>
            <Label>Review notes</Label>
            <Textarea data-testid="trial-final-notes" rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Summarise the review decision and supporting evidence." />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="submit" data-testid="trial-final-submit" disabled={busy} className="bg-[#1A2744] hover:bg-[#0F1A30]">
              {busy ? "Saving…" : "Submit review"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminTrials() {
  const { user } = useAuth();
  const isManagerOnly = user?.role === "MANAGER";
  const [trials, setTrials] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("ACTIVE_ALL");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [tRes, uRes] = await Promise.all([
        api.get("/trials"),
        api.get("/trials/eligible-employees"),
      ]);
      setTrials(tRes.data.trials || []);
      setEmployees(uRes.data.users || []);
    } catch {
      toast.error("Failed to load trials");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = trials.filter((t) => {
    if (filter === "ACTIVE_ALL") return ["ACTIVE", "AUTO_FLAGGED"].includes(t.status);
    if (filter === "ALL") return true;
    return t.status === filter;
  });

  const counts = trials.reduce(
    (acc, t) => { acc[t.status] = (acc[t.status] || 0) + 1; return acc; },
    {},
  );

  return (
    <div className="space-y-8" data-testid="admin-trials-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold text-[#1A2744]">90-day trial tracker</h1>
          <p className="text-sm text-gray-500 mt-1">
            {isManagerOnly
              ? "Trials for employees in your site only."
              : "All employee 90-day trials across both sites."}
          </p>
        </div>
        <InitiateDialog employees={employees} onSaved={load} />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { k: "ACTIVE", label: "Active" },
          { k: "AUTO_FLAGGED", label: "Action needed" },
          { k: "PASSED", label: "Passed" },
          { k: "NOT_PASSED", label: "Not passed" },
        ].map((s) => (
          <Card key={s.k} className="border-gray-200">
            <CardContent className="p-4">
              <div className="text-xs uppercase tracking-wide text-gray-500">{s.label}</div>
              <div className="text-2xl font-bold text-[#1A2744]" data-testid={`trial-stat-${s.k}`}>{counts[s.k] || 0}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
            <h3 className="font-heading text-lg font-bold text-[#1A2744]">Trials</h3>
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-44" data-testid="trial-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ACTIVE_ALL">Active + flagged</SelectItem>
                <SelectItem value="ACTIVE">Active only</SelectItem>
                <SelectItem value="AUTO_FLAGGED">Action needed</SelectItem>
                <SelectItem value="PASSED">Passed</SelectItem>
                <SelectItem value="NOT_PASSED">Not passed</SelectItem>
                <SelectItem value="ALL">All</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Employee</TableHead>
                <TableHead>Start</TableHead>
                <TableHead>Day-90 end</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Mid-trial check-in</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={6} className="text-center py-8 text-gray-500">Loading…</TableCell></TableRow>}
              {!loading && filtered.length === 0 && (
                <TableRow><TableCell colSpan={6} className="text-center py-12 text-gray-500">
                  <CalendarClock className="h-8 w-8 mx-auto mb-2 opacity-40" /> No trial records to show.
                </TableCell></TableRow>
              )}
              {filtered.map((t) => (
                <TableRow key={t.id} data-testid={`trial-row-${t.id}`}>
                  <TableCell>
                    <div className="font-medium text-[#1A2744]">{t.employee_code}</div>
                    <div className="text-xs text-gray-500">{t.user_name}</div>
                  </TableCell>
                  <TableCell className="text-sm">{t.start_date}</TableCell>
                  <TableCell className="text-sm">
                    <div>{t.end_date}</div>
                    {t.days_remaining !== null && t.days_remaining !== undefined && (
                      <div className="text-xs text-gray-500">
                        {t.days_remaining < 0 ? `${-t.days_remaining}d ago` : `${t.days_remaining}d left`}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge className={`border ${LIGHT_STYLES[t.traffic_light] || ""}`}>
                      {STATUS_LABEL[t.status] || t.status}
                    </Badge>
                    {t.final_decision && (
                      <div className="text-[10px] text-gray-500 mt-1">{t.final_review_date?.slice(0, 10)} · {t.final_review_by_name}</div>
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-gray-600 max-w-xs">
                    {t.midtrial_checkin_date ? (
                      <>
                        <div className="font-medium text-[#1A2744]">{t.midtrial_checkin_date.slice(0, 10)} · {t.midtrial_checkin_by_name}</div>
                        <div className="truncate">{t.midtrial_checkin_notes}</div>
                      </>
                    ) : (
                      <span className="text-gray-400">No check-in yet</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right space-x-2">
                    {(t.status === "ACTIVE" || t.status === "AUTO_FLAGGED") && (
                      <>
                        <CheckinDialog trial={t} onSaved={load} />
                        <FinalReviewDialog trial={t} onSaved={load} />
                      </>
                    )}
                    {(t.status === "PASSED" || t.status === "NOT_PASSED") && (
                      <span className="text-xs text-gray-500">Closed</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
